import { Tooltip } from "bootstrap";

const COOKIE_NAME = "admin-sidebar-collapsed";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Libellé de l'infobulle d'un lien de menu en mode replié.
 *
 * Replié, `.bi-lock-fill` passe en `display: none` (cf. `admin-sidebar.scss`) : son
 * infobulle « Non inclus dans votre plan » devient inatteignable, et le lien grisé dit
 * qu'un module est différent sans dire pourquoi. On reporte donc la raison du cadenas
 * sur l'infobulle du lien, qui est la seule encore atteignable.
 */
export function collapsedTooltipTitle(link: HTMLElement): string {
    const label = link.dataset.bsTitle ?? link.querySelector(".nav-label")?.textContent?.trim() ?? "";
    const lock = link.querySelector<HTMLElement>(".bi-lock-fill")?.dataset.bsTitle?.trim() ?? "";
    if ("" === lock) {
        return label;
    }

    return "" === label ? lock : `${label} — ${lock}`;
}

export default function initAdminSidebar(): void {
    const admin = document.getElementById("admin");
    const toggle = document.getElementById("admin-sidebar-toggle");
    const icon = toggle?.querySelector(".admin-sidebar-toggle-icon");
    if (!admin || !toggle || !icon) {
        return;
    }

    const links = admin.querySelectorAll<HTMLElement>("#admin-sidebar .nav-link");

    // Les libellés sont masqués en mode replié : on les remonte en infobulle, avec la
    // raison du cadenas quand le module est hors plan (son icône est masquée elle aussi).
    const applyTooltips = (collapsed: boolean): void => {
        links.forEach(link => {
            const tooltip = Tooltip.getInstance(link);
            if (!collapsed) {
                tooltip?.dispose();
            } else if (!tooltip) {
                new Tooltip(link, {
                    placement: "right",
                    title: collapsedTooltipTitle(link),
                });
            }
        });
    };

    // ‹ (chevron gauche) quand déplié → replier ; › (chevron droit) quand replié → déplier.
    const apply = (collapsed: boolean): void => {
        admin.classList.toggle("is-sidebar-collapsed", collapsed);
        icon.classList.toggle("bi-chevron-left", !collapsed);
        icon.classList.toggle("bi-chevron-right", collapsed);
        toggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
        applyTooltips(collapsed);
    };

    // L'état initial vient du cookie, appliqué côté Twig : rien à repositionner ici.
    applyTooltips(admin.classList.contains("is-sidebar-collapsed"));

    // Active les transitions après le 1er rendu (évite l'animation au chargement).
    requestAnimationFrame(() => admin.classList.add("is-sidebar-animated"));

    toggle.addEventListener("click", () => {
        const collapsed = !admin.classList.contains("is-sidebar-collapsed");
        apply(collapsed);
        // Cookie plutôt que localStorage : le serveur connaît l'état et pose la classe dès
        // le rendu (pas de flash), et l'écriture ne lève pas quand le stockage est bloqué.
        const secure = "https:" === location.protocol ? "; Secure" : "";
        document.cookie = `${COOKIE_NAME}=${collapsed ? "1" : "0"}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
    });
}
